import React, { useEffect, useState, useRef } from 'react'
import styles from '@/styles/Notification.module.scss'
import { BsSearch, BsXLg, BsArrowLeft } from 'react-icons/bs'
import { FaRunning, FaRegStar, FaStar } from 'react-icons/fa'
import Loader from '@/components/Loader'
import { format } from 'date-fns'
import { supabase } from '@/client'
import { useLoginValue } from '@/hooks/useLoginValue'

const ATTENDANCE_STATUS = {
  ANNUAL_TIME_OFF: '휴가', // 연차
  CUSTOM_TIME_OFF: '휴가', // 공가
  833692: '사무실근무',
  833695: '재택근무',
}

const EmployeeAttendance = () => {
  const { ipcRenderer } = window.electron

  const { userInfo } = useLoginValue()

  const [users, setUsers] = useState([])
  const [searchResults, setSearchResults] = useState([])

  const updateFavUrs = async () => {
    try {
      const { data: favData, error } = await supabase.from('favorite').select('target_member_id').eq('user_id', userInfo.id)

      if (error) {
        console.error('Failed to fetch favorites:', error)
        return
      }

      if (favData) {
        setFavoriteUsers(favData.map((item) => item.target_member_id))
      }
    } catch (err) {
      console.error('Error in updateFavUrs:', err)
    }
  }

  useEffect(() => {
    ;(async () => {
      if (userInfo) {
        updateFavUrs()
      }
    })()
  }, [userInfo])

  const fetchWorkInfoHandler = async (event, data) => {
    if (!data || !data.allUsrTimeTracking || !data.allUsrs) {
      return
    }

    const { allUsrTimeTracking, allUsrs } = data
    const { data: scheduleData } = await supabase.from('work_schedule').select(`
       expected_end_time,
       updated_at,
       users (
        id,
        name,
        email
       )
       `)

    const allUsrsInfo = allUsrTimeTracking.map((item) => {
      const isWorkedToday = item.records.length > 0
      const stopRecord = isWorkedToday && item.records?.[0].workClockRecordPacks?.[0].stopRecord
      const {
        displayName = '',
        email = '',
        userIdHash = '',
        jobRoles = '',
        departmentIdHashes = '',
        jobRank = '',
        jobTitle = '',
      } = allUsrs.find((user) => user.userIdHash === item.userIdHash)

      const expectedWeTime = scheduleData.find((expectedData) => expectedData.users.email === email)?.expected_end_time
      return {
        name: displayName,
        userIdHash,
        jobRole: jobRoles?.[0]?.name,
        department: departmentIdHashes?.[0]?.name,
        jobRank: jobRank?.[0]?.name,
        jobTitle: jobTitle?.[0]?.name,
        // organization: allUrs.find((user) => userIdHash === item.userIdHash).displayName, // TODO 부서 정보 추가 필요
        wsTime: isWorkedToday ? format(new Date(item?.records[0].workClockRecordPacks[0].startRecord.targetTime), 'HH:mm') : null,
        weTime: stopRecord ? format(new Date(stopRecord.targetTime), 'HH:mm') : null,
        expectedWeTime: expectedWeTime ? format(new Date(expectedWeTime), 'HH:mm') : null,
        attendanceStatus: item?.dailySchedules?.[0]?.timeBlocks?.[0] ? ATTENDANCE_STATUS[item?.dailySchedules[0].timeBlocks[0].type] : null,
        isRemoteWork: isWorkedToday ? item?.records[0].workClockRecordPacks[0].startRecord.customerWorkFormId === 833695 : false,
      }
    })
    setUsers(allUsrsInfo)
  }

  const [isResultMode, setIsResultMode] = useState(false)
  const [searchTarget, setSearchTarget] = useState('')
  const [currentSearchTarget, setCurrentSearchTarget] = useState('')

  const clear = () => {
    setSearchTarget('')
  }

  const goList = () => {
    clear()
    setIsResultMode(false)
    setSearchResults([])
    setIsFavoritesOnly(false)
  }

  const search = () => {
    setIsResultMode(true)
    setCurrentSearchTarget(searchTarget)
    setSearchResults(users.filter((user) => user.name.includes(searchTarget)))
  }

  const [favoriteUsers, setFavoriteUsers] = useState([])
  const [isFavoritesOnly, setIsFavoritesOnly] = useState(false)

  useEffect(() => {
    if (!isFavoritesOnly) {
      clear()
      setIsResultMode(false)
      setSearchResults([])
    }
  }, [isFavoritesOnly])

  useEffect(() => {
    if (isFavoritesOnly && !favoriteUsers.length) {
      goList()
    }
  }, [favoriteUsers])

  const fetchData = async (event, command) => {
    if (command) ipcRenderer.send('fetchData')
  }
  useEffect(() => {
    ipcRenderer.on('isShow', fetchData)
    ipcRenderer.on('fetchWorkInfo', fetchWorkInfoHandler)

    return () => {
      ipcRenderer.removeListener('isShow', fetchData)
      ipcRenderer.removeListener('fetchWorkInfo', fetchWorkInfoHandler)
    }
  }, [])

  const toggleFavorite = async (event, { userIdHash }) => {
    try {
      const { data } = await supabase.from('favorite').select('*').eq('user_id', userInfo.id).eq('target_member_id', userIdHash)

      if (data.length > 0) {
        // Favorite exists, so delete it
        const { error: deleteError } = await supabase
          .from('favorite')
          .delete()
          .eq('user_id', userInfo.id)
          .eq('target_member_id', userIdHash)

        if (deleteError) {
          console.error('Failed to delete favorite:', deleteError)
          return
        }
      } else {
        // Favorite doesn't exist, so insert it
        const { error: insertError } = await supabase.from('favorite').insert({ user_id: userInfo.id, target_member_id: userIdHash })

        if (insertError) {
          console.error('Failed to insert favorite:', insertError)
          return
        }
      }

      // Wait for the favorites to be updated
      await updateFavUrs()
    } catch (err) {
      console.error('Error in toggleFavorite:', err)
    }
  }

  // Member component
  const Card = ({ item }) => {
    const isWorkingNow = item.wsTime && !item.weTime
    return (
      <div className="mt-2 py-1 hover:bg-gray-100/25">
        <div className="flex">
          <div className="flex justify-between items-center w-full px-1">
            <div className="flex items-center pl-4">
              <div className="flex">
                <span className="mt-0.5">{isWorkingNow ? '🟢' : '🔴'}</span>
                <div className="flex flex-col ml-1">
                  <div className="flex items-center">
                    <span className="font-semibold">
                      {item.name} {item.isRemoteWork ? '🏡' : ''}
                    </span>
                    {!isWorkingNow && item.attendanceStatus ? (
                      <span className="inline-block ml-1.5 px-1 rounded-sm bg-gray-200 text-black text-xs font-semibold">
                        {item.attendanceStatus}
                      </span>
                    ) : isWorkingNow && (item.wsTime || item.expectedWeTime) ? (
                      <div className="flex items-center ml-2">
                        <FaRunning className="text-sm text-yellow-500" />{' '}
                        <span className="ml-0.5 text-[12px] text-yellow-500">{item.wsTime} ~</span>
                        {item.expectedWeTime ? (
                          <span className="ml-1 font-semibold text-[12px] text-yellow-500">{item.expectedWeTime} 퇴근 예정</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <span className="text-xs opacity-80">
                    {[item.department, item.jobRole, item.jobRank, item.jobTitle]
                      .filter((infoItem) => infoItem)
                      .map((infoItem, infoItemIndex) => {
                        return infoItemIndex !== 0 && infoItem ? ` / ${infoItem}` : infoItem
                      })}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" onClick={(e) => toggleFavorite(e, item)}>
              {favoriteUsers.find((favUrsHashId) => {
                return favUrsHashId === item.userIdHash
              }) ? (
                <FaStar className="text-yellow-500" />
              ) : (
                <FaRegStar />
              )}
              <span className="sr-only">즐겨찾기</span>
            </button>
            {/* <button type="button" className="after-content-[''] absolute top-0 left-0 z-10 w-full h-full hover:bg-gray-50/20 rounded-md">
              <span className="sr-only">구성원 상세 정보 보기</span>
            </button> */}
          </div>
        </div>
        <div className="hidden">
          <div>출근시간: {item.wsTime}</div>
          <div>퇴근시간: {item.weTime}</div>
          <div>근무형태: {item.attendanceStatus}</div>
        </div>
      </div>
    )
  }

  return (
    <div className={`${styles['noti']}`}>
      <div className={`${styles['noti__top-box']}`}>
        {(!!isResultMode || isFavoritesOnly) && (
          <button type="button" className="mr-2" onClick={goList}>
            <BsArrowLeft />
          </button>
        )}
        {!isFavoritesOnly && (
          <input
            type="text"
            className="w-full pr-2"
            placeholder="구성원 이름을 입력해주세요."
            value={searchTarget}
            onChange={(e) => setSearchTarget(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                search()
              }
              if (e.key === 'Escape') {
                goList()
              }
            }}
          />
        )}
        {!searchTarget && favoriteUsers.length > 0 && (
          <div className={`flex justify-between items-center ${isFavoritesOnly ? 'w-full' : ''}`}>
            {isFavoritesOnly && <p className="text-md font-bold">즐겨찾기</p>}
            <button
              type="button"
              className={`flex items-center ml-2 px-2 py-1 rounded text-sm font-medium transition-colors ${
                isFavoritesOnly
                  ? 'bg-yellow-400 hover:bg-yellow-600 text-gray-900 border-yellow-600'
                  : 'hover:bg-yellow-600 hover:text-gray-900'
              }`}
              onClick={() => setIsFavoritesOnly(!isFavoritesOnly)}
            >
              <span>
                <FaStar />
              </span>{' '}
              <span className="ml-2 text-sm">{favoriteUsers.length}</span>
            </button>
          </div>
        )}
        {!!searchTarget && (
          <div className="flex gap-2">
            <button type="button" className={styles['noti__close-btn']} onClick={clear}>
              <BsXLg />
            </button>
            <button type="button" className={styles['noti__close-btn']} onClick={search}>
              <BsSearch />
            </button>
          </div>
        )}
      </div>
      <div className={`${styles['noti__box']} -ml-4`}>
        <div className={'c-list'}>
          {isResultMode && (
            <p className="mb-2 ml-4 text-sm">
              &quot;
              {currentSearchTarget}&quot;(으)로 검색한 결과입니다.
            </p>
          )}
          {(isResultMode
            ? searchResults
            : isFavoritesOnly
            ? users.filter((item) => favoriteUsers.find((favUsrItem) => favUsrItem === item.userIdHash))
            : users
          )?.map((item, index) => {
            return <Card item={item} key={index} />
          })}
          {isResultMode && !searchResults.length && (
            <div className="flex justify-center items-center w-full h-[calc(100vh-80px)]">
              <p>결과가 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmployeeAttendance
